import React from 'react';
import type { HealthClub, Organization, Thing as SchemaObject, WithContext } from 'schema-dts';

import type { ClubCmsRecord } from 'src/content/cms-provider/club-cms-provider';
import type { SiteConfig } from 'src/context/site-context';
import { BuildType, getCountryCodeForBuildType } from 'src/enums/build/build-type';
import type { Language } from 'src/enums/language';
import { getDisplayTextForLanguage } from 'src/enums/language';
import { getAbsoluteClubPagePath } from 'src/helpers/navigation-helper';

import type { ContactDetails } from '../common/contact-details';

import {
  CLUB_PHONE_NON_MEMBERS_DESCRIPTION,
  getAddressParts,
  getClubPhoneNumber,
  getContactDetails,
  HEAD_OFFICE_PHONE_NUMBER,
} from './structured-data-helper';

import HeaderLevelTwoMenuItem = Queries.HeaderLevelTwoMenuItem;
import HeaderLevelThreeMenuItem = Queries.HeaderLevelThreeMenuItem;

export const STRUCTURED_DATA_CONTEXT = 'https://schema.org';
const SCRIPT_TYPE = 'application/ld+json';

const getNestedMenuItems = (menuItems?: readonly HeaderLevelTwoMenuItem[]): string[] => {
  const facilities = new Set<string>();

  const visit = (
    list: readonly HeaderLevelTwoMenuItem[] | readonly HeaderLevelThreeMenuItem[]
  ): void => {
    list.forEach((item) => {
      const name = item.name.trim();
      if (name && item.isSeoAmenity === true) {
        facilities.add(name);
      }
      if ('levelThreeMenuItems' in item) {
        visit((item as HeaderLevelTwoMenuItem).levelThreeMenuItems ?? []);
      }
    });
  };

  visit(menuItems ? [menuItems].flat() : []);
  return [...facilities];
};

const getStructuredDataForBuildType = (buildType: BuildType) => {
  switch (buildType) {
    case BuildType.MERIDIAN:
      return {
        name: 'Meridian Spa & Fitness Deutschland GmbH',
        clubName: 'Meridian Spa & Fitness',
        url: 'https://www.meridianspa.de/',
        logo: 'https://www.meridianspa.de/fixed-images/meridian-logo-dark-bg.png',
        sameAs: [
          'https://de-de.facebook.com/meridianspa/',
          'https://www.instagram.com/meridianspa/?hl=de',
          'https://www.youtube.com/MeridianSpa',
        ],
      };
    case BuildType.TOGETHER_FOUNDATION:
      return {
        name: 'Together Foundation',
        clubName: 'Together Foundation',
        url: 'https://www.dlctogetherfoundation.org/',
        logo: 'https://www.dlctogetherfoundation.org/fixed-images/together-foundation-logo-black.png',
        sameAs: [],
      };
    default:
      return {
        name: 'David Lloyd Clubs',
        clubName: 'David Lloyd',
        sameAs: [
          'https://www.facebook.com/davidlloydclubsuk',
          'https://twitter.com/DavidLloydUK',
          'https://www.youtube.com/user/davidlloydleisureltd',
        ],
        url: 'https://www.davidlloyd.co.uk',
        logo: 'https://www.davidlloyd.co.uk/fixed-images/david-lloyd-logo-dark.png',
      };
  }
};

const getOrganisationStructuredData = (buildType: BuildType): WithContext<Organization> => {
  const { name, url, logo, sameAs } = getStructuredDataForBuildType(buildType);
  return {
    '@context': STRUCTURED_DATA_CONTEXT,
    '@type': 'Organization',
    'name': name,
    'url': url,
    'logo': logo,
    'sameAs': sameAs,
    'contactPoint': {
      '@type': 'ContactPoint',
      'areaServed': 'GB',
      'availableLanguage': 'en',
      'contactType': 'customer support',
      'telephone': `${HEAD_OFFICE_PHONE_NUMBER}`,
    },
  };
};

const getClubStructuredData = (
  buildType: BuildType,
  contactDetails: ContactDetails,
  language: Language,
  siteId: string,
  headerFacilities?: string[]
): WithContext<HealthClub> => {
  const clubPhoneNumber = getClubPhoneNumber(
    contactDetails.clubPhoneNumbers,
    CLUB_PHONE_NON_MEMBERS_DESCRIPTION
  );
  const { streetAddress, postCode } = getAddressParts(contactDetails);
  const { clubName: buildTypeClubName, logo } = getStructuredDataForBuildType(buildType);
  const countryCode = getCountryCodeForBuildType(buildType);
  const amenityFeature = headerFacilities?.length
    ? {
        amenityFeature: headerFacilities.map((name) => ({
          '@type': 'LocationFeatureSpecification',
          'name': name,
          'value': true,
        })),
      }
    : {};

  return {
    '@context': STRUCTURED_DATA_CONTEXT,
    '@type': 'HealthClub',
    'name': `${buildTypeClubName} ${contactDetails.clubName}`,
    'logo': logo,
    'url': getAbsoluteClubPagePath('/', siteId, language),
    'address': {
      '@type': 'PostalAddress',
      'streetAddress': `${streetAddress}`,
      'postalCode': `${postCode}`,
      'addressCountry': countryCode,
    },
    'telephone': `${clubPhoneNumber}`,
    'contactPoint': {
      '@type': 'ContactPoint',
      'areaServed': countryCode,
      'availableLanguage': getDisplayTextForLanguage(language),
      'contactType': 'customer support',
      'telephone': `${clubPhoneNumber}`,
      'email': `${contactDetails.clubEmail}`,
    },
    ...amenityFeature,
  } as WithContext<HealthClub>;
};

export type SEOClubStructuredData = Pick<
  ClubCmsRecord,
  'clubDetails' | 'newSharedContent' | 'sharedContent' | 'sharedLandingContent'
> &
  Partial<Pick<ClubCmsRecord, 'headerContent'>>;

type StructuredDataProps = {
  siteConfig: SiteConfig;
  cmsContent: SEOClubStructuredData;
};

export const StructuredData: React.FC<StructuredDataProps> = ({ siteConfig, cmsContent }) => {
  const { buildType, language, siteId } = siteConfig;
  const contactDetails: ContactDetails = getContactDetails(cmsContent, siteId);
  const headerFacilities = getNestedMenuItems(
    cmsContent?.headerContent?.headerMenuItems?.flatMap((menu) => menu.levelTwoMenuItems ?? [])
  );
  const organisationStructuredData = getOrganisationStructuredData(buildType);
  const clubStructuredData = getClubStructuredData(
    buildType,
    contactDetails,
    language,
    siteId,
    headerFacilities
  );

  return (
    contactDetails.clubName !== 'promo' &&
    contactDetails.clubName?.trim() !== '' && (
      <>
        <StructuredDataObject structuredData={organisationStructuredData} />
        <StructuredDataObject structuredData={clubStructuredData} />
      </>
    )
  );
};

type StructuredDataObjectProps = {
  structuredData: WithContext<SchemaObject> | null;
};

export const StructuredDataObject = ({ structuredData }: StructuredDataObjectProps) => {
  if (!structuredData) {
    return null;
  }

  return (
    <script
      type={SCRIPT_TYPE}
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
};
